import { z } from "zod";
import { LISTING_CATEGORIES } from "./listing-category";
import { ShipFromAddressSchema } from "./ship-from-address";

/**
 * Listing-search / feed wire contract — canonical shape served by
 * `GET /api/feed/listings` (browse, discover, search, and the `search_listings`
 * RPC path). Promoted out of `hooks/use-paginated-feed.ts` so the type has one
 * home (SEARCH_UNIFICATION_PLAN §2.1). Mobile `apps/mobile/types/feed.ts`
 * currently declares a *subset* — reconcile it against this in a later pass.
 *
 * Schema-first: the types in this file are derived from the schemas
 * (`z.infer<typeof XSchema>`), so the bidirectional `pinned()` wrapper used
 * elsewhere in this package would create a circular type reference. Drift on
 * a schema-first surface is structurally impossible — the type IS the
 * schema's inferred shape. ESLint disables below are deliberate per-schema
 * documentation. See pin.ts header.
 */
// eslint-disable-next-line no-restricted-syntax -- schema-first declaration: type inferred from schema, no separate interface to pin against.
export const ListingCardDetailsSchema = z.object({
  tcg_name: z.string().nullable().optional(),
  set_name: z.string().nullable().optional(),
  rarity_name: z.string().nullable().optional(),
  condition_name: z.string().nullable().optional(),
  grading_company_name: z.string().nullable().optional(),
  grade_value: z.number().nullable().optional(),
  grade_label: z.string().nullable().optional(),
  badges: z.array(z.string()).optional(),
  defects: z.array(z.string()).optional(),
});

// eslint-disable-next-line no-restricted-syntax -- schema-first declaration: ListingResult is z.infer<typeof ListingResultSchema>.
export const ListingResultSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  price: z.string(),
  currency: z.string().nullable().optional(),
  image_urls: z.array(z.string()),
  // Structured ship-from address — replaces free-text `location` per
  // ADR_LISTING_SHIP_FROM_ADDRESS.md Phase 2. `null` for orphan listings
  // (seller has no main address and no per-listing pin).
  shipFromAddress: ShipFromAddressSchema,
  condition: z.string(),
  grade: z.string(),
  created_at: z.string(),
  user_id: z.string(),
  quantity: z.number(),
  sellerUsername: z.string().optional(),
  sellerAvatarUrl: z.string().optional(),
  sellerIsFollowing: z.boolean().optional(),
  initialSaved: z.boolean(),
  // Saved (bookmark) count, computed server-side via get_listing_saved_counts
  // (see lib/listings/saved-counts.ts). Optional: the route omits it when the
  // RPC soft-fails and consumers degrade to "no count". Without this field the
  // boundary parse would silently STRIP it (Zod default), turning the server's
  // work into a no-op — cards would fall back to per-card count queries.
  saves_count: z.number().int().nonnegative().optional(),
  scheduled_publish_at: z.string().nullable().optional(),
  brand_id: z.string().nullable().optional(),
  theme_id: z.string().nullable().optional(),
  product_type_id: z.string().nullable().optional(),
  brand_slug: z.string().nullable().optional(),
  brand_name: z.string().nullable().optional(),
  theme_slug: z.string().nullable().optional(),
  theme_name: z.string().nullable().optional(),
  product_type_slug: z.string().nullable().optional(),
  product_type_name: z.string().nullable().optional(),
  listing_format: z.string().nullable().optional(),
  // accepts_offers dropped in Phase 3 (2026-06-29) — derive from listing_format
  // via isOfferEnabledFormat(). See DISCRIMINATORS_HYGIENE_IMPACT.md §2 Item 3.
  offers_pending_count: z.number().optional(),
  type: z.string().nullable().optional(),
  auction_start_price: z.number().nullable().optional(),
  auction_highest_bid: z.number().nullable().optional(),
  auction_end_at: z.string().nullable().optional(),
  auction_status: z.string().nullable().optional(),
  auction_bid_count: z.number().nullable().optional(),
  sellerSalesCount: z.number().optional(),
  card_details: ListingCardDetailsSchema.nullable().optional(),
});
export type ListingResult = z.infer<typeof ListingResultSchema>;

// eslint-disable-next-line no-restricted-syntax -- schema-first declaration: FeedFilterState is z.infer<typeof FeedFilterStateSchema>.
export const FeedFilterStateSchema = z.object({
  brands: z.array(z.string()),
  themes: z.array(z.string()),
  categories: z.array(z.string()),
  tcgs: z.array(z.string()).optional(),
  conditions: z.array(z.string()),
  gradeRange: z.tuple([z.number(), z.number()]).nullable(),
  listingFormats: z.array(z.string()).optional(),
  tcgSets: z.array(z.string()).optional(),
  tcgRarities: z.array(z.string()).optional(),
  cardConditions: z.array(z.string()).optional(),
  cardLanguages: z.array(z.string()).optional(),
  cardFormats: z.array(z.string()).optional(),
  gradingCompanies: z.array(z.string()).optional(),
  cardGradeRange: z.tuple([z.number(), z.number()]).nullable().optional(),
  listingCategory: z.enum(LISTING_CATEGORIES).nullable().optional(),
  mtgPrintVariants: z.array(z.string()).optional(),
});
export type FeedFilterState = z.infer<typeof FeedFilterStateSchema>;

// eslint-disable-next-line no-restricted-syntax -- schema-first declaration: ListingFeedResponse is z.infer<typeof ListingFeedResponseSchema>.
export const ListingFeedResponseSchema = z.object({
  items: z.array(ListingResultSchema),
  hasNextPage: z.boolean(),
  page: z.number(),
  totalCount: z.number().nullable(),
  nextCursor: z.string().nullable().optional(),
});
export type ListingFeedResponse = z.infer<typeof ListingFeedResponseSchema>;
